import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "@/contexts/OrganizationContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Bookmark,
  ExternalLink,
  Trash2,
  FolderOpen,
  Plus,
  Edit2,
  Search,
  FolderPlus,
  MoreVertical,
} from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import MoveToFolderDialog from "@/components/dashboard/MoveToFolderDialog";

interface Folder {
  id: string;
  name: string;
  created_at: string;
}

interface SavedResource {
  id: string;
  folder_id: string | null;
  notes: string | null;
  created_at: string;
  resource: {
    id: string;
    title: string;
    description: string;
    slug: string | null;
    type: string;
    thumbnail_url: string | null;
  };
}

const MyCollection: React.FC = () => {
  const queryClient = useQueryClient();
  const { organization } = useOrganization();
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [newFolderName, setNewFolderName] = useState("");
  const [folderDialogOpen, setFolderDialogOpen] = useState(false);
  const [editingFolder, setEditingFolder] = useState<Folder | null>(null);
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);
  const [selectedResourceId, setSelectedResourceId] = useState<string | null>(null);
  const [selectedResourceFolderId, setSelectedResourceFolderId] = useState<string | null>(null);

  // Fetch folders
  const { data: folders } = useQuery({
    queryKey: ["user-folders"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { data, error } = await supabase
        .from("user_folders")
        .select("id, name, created_at")
        .eq("user_id", user.id)
        .order("name", { ascending: true });

      if (error) throw error;
      return data as Folder[];
    },
  });

  // Fetch saved resources with folder counts
  const { data: savedResources, isLoading } = useQuery({
    queryKey: ["my-collection"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { data, error } = await supabase
        .from("user_saved_resources")
        .select(`
          id,
          folder_id,
          notes,
          created_at,
          resource:library_items(id, title, description, slug, type, thumbnail_url)
        `)
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data as unknown as SavedResource[];
    },
  });

  // Calculate folder counts
  const folderCounts = React.useMemo(() => {
    const counts: Record<string, number> = { uncategorized: 0 };
    savedResources?.forEach((sr) => {
      if (sr.folder_id) {
        counts[sr.folder_id] = (counts[sr.folder_id] || 0) + 1;
      } else {
        counts.uncategorized += 1;
      }
    });
    return counts;
  }, [savedResources]);

  // Filter resources
  const filteredResources = React.useMemo(() => {
    let resources = savedResources ?? [];

    // Filter by folder
    if (selectedFolderId === "uncategorized") {
      resources = resources.filter((sr) => !sr.folder_id);
    } else if (selectedFolderId) {
      resources = resources.filter((sr) => sr.folder_id === selectedFolderId);
    }

    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      resources = resources.filter(
        (sr) =>
          sr.resource?.title?.toLowerCase().includes(query) ||
          sr.resource?.description?.toLowerCase().includes(query)
      );
    }

    return resources;
  }, [savedResources, selectedFolderId, searchQuery]);

  // Folder mutations
  const createFolderMutation = useMutation({
    mutationFn: async (name: string) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { error } = await supabase.from("user_folders").insert({
        name,
        user_id: user.id,
        organization_id: organization?.id,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user-folders"] });
      setNewFolderName("");
      setFolderDialogOpen(false);
      toast.success("Folder created");
    },
    onError: () => {
      toast.error("Failed to create folder");
    },
  });

  const updateFolderMutation = useMutation({
    mutationFn: async ({ id, name }: { id: string; name: string }) => {
      const { error } = await supabase
        .from("user_folders")
        .update({ name })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user-folders"] });
      setEditingFolder(null);
      toast.success("Folder updated");
    },
    onError: () => {
      toast.error("Failed to update folder");
    },
  });

  const deleteFolderMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("user_folders").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user-folders"] });
      queryClient.invalidateQueries({ queryKey: ["my-collection"] });
      if (selectedFolderId === editingFolder?.id) {
        setSelectedFolderId(null);
      }
      toast.success("Folder deleted");
    },
    onError: () => {
      toast.error("Failed to delete folder");
    },
  });

  const removeResourceMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("user_saved_resources")
        .delete()
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["my-collection"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard-stats"] });
      toast.success("Resource removed from collection");
    },
    onError: () => {
      toast.error("Failed to remove resource");
    },
  });

  const handleOpenMoveDialog = (resourceId: string, currentFolderId: string | null) => {
    setSelectedResourceId(resourceId);
    setSelectedResourceFolderId(currentFolderId);
    setMoveDialogOpen(true);
  };

  return (
    <div className="p-4 md:p-6 lg:p-8">
      {/* Header */}
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-foreground flex items-center gap-3">
            <Bookmark className="h-7 w-7 text-primary" />
            My Collection
          </h1>
          <p className="text-muted-foreground mt-1">
            Your saved resources organized by folder.
          </p>
        </div>
        <div className="flex gap-2">
          <Dialog open={folderDialogOpen} onOpenChange={setFolderDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm">
                <FolderPlus className="h-4 w-4 mr-2" />
                New Folder
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create New Folder</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 pt-4">
                <Input
                  placeholder="Folder name"
                  value={newFolderName}
                  onChange={(e) => setNewFolderName(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && newFolderName.trim() && createFolderMutation.mutate(newFolderName.trim())}
                />
                <Button
                  onClick={() => createFolderMutation.mutate(newFolderName.trim())}
                  disabled={!newFolderName.trim() || createFolderMutation.isPending}
                >
                  Create Folder
                </Button>
              </div>
            </DialogContent>
          </Dialog>
          <Link to="/library">
            <Button size="sm">
              <Plus className="h-4 w-4 mr-2" />
              Browse Library
            </Button>
          </Link>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        {/* Folder Sidebar - Desktop */}
        <aside className="hidden lg:block w-64 shrink-0">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Folders</CardTitle>
            </CardHeader>
            <CardContent className="p-2">
              <ScrollArea className="max-h-[400px]">
                <div className="space-y-1">
                  {/* All Resources */}
                  <Button
                    variant={selectedFolderId === null ? "secondary" : "ghost"}
                    className="w-full justify-start gap-2 h-9"
                    onClick={() => setSelectedFolderId(null)}
                  >
                    <Bookmark className="h-4 w-4 text-primary" />
                    <span className="flex-1 text-left text-sm">All Resources</span>
                    <span className="text-xs text-muted-foreground">
                      {savedResources?.length ?? 0}
                    </span>
                  </Button>

                  {/* Uncategorized */}
                  <Button
                    variant={selectedFolderId === "uncategorized" ? "secondary" : "ghost"}
                    className="w-full justify-start gap-2 h-9"
                    onClick={() => setSelectedFolderId("uncategorized")}
                  >
                    <FolderOpen className="h-4 w-4 text-muted-foreground" />
                    <span className="flex-1 text-left text-sm">Uncategorized</span>
                    <span className="text-xs text-muted-foreground">
                      {folderCounts.uncategorized}
                    </span>
                  </Button>

                  {/* User folders */}
                  {folders?.map((folder) => (
                    <div key={folder.id} className="group flex items-center">
                      <Button
                        variant={selectedFolderId === folder.id ? "secondary" : "ghost"}
                        className="flex-1 justify-start gap-2 h-9"
                        onClick={() => setSelectedFolderId(folder.id)}
                      >
                        <FolderOpen className="h-4 w-4 text-primary" />
                        <span className="flex-1 text-left text-sm truncate">
                          {folder.name}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {folderCounts[folder.id] ?? 0}
                        </span>
                      </Button>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => setEditingFolder(folder)}>
                            <Edit2 className="h-4 w-4 mr-2" />
                            Rename
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            className="text-destructive"
                            onClick={() => deleteFolderMutation.mutate(folder.id)}
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </aside>

        {/* Mobile Folder Filter */}
        <div className="lg:hidden">
          <ScrollArea className="pb-2">
            <div className="flex gap-2">
              <Button
                variant={selectedFolderId === null ? "default" : "outline"}
                size="sm"
                onClick={() => setSelectedFolderId(null)}
              >
                All ({savedResources?.length ?? 0})
              </Button>
              <Button
                variant={selectedFolderId === "uncategorized" ? "default" : "outline"}
                size="sm"
                onClick={() => setSelectedFolderId("uncategorized")}
              >
                Uncategorized ({folderCounts.uncategorized})
              </Button>
              {folders?.map((folder) => (
                <Button
                  key={folder.id}
                  variant={selectedFolderId === folder.id ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSelectedFolderId(folder.id)}
                >
                  {folder.name} ({folderCounts[folder.id] ?? 0})
                </Button>
              ))}
            </div>
          </ScrollArea>
        </div>

        {/* Main Content */}
        <div className="flex-1 min-w-0">
          {/* Search */}
          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search your collection..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          {/* Resources Grid */}
          {isLoading ? (
            <div className="text-muted-foreground">Loading...</div>
          ) : !filteredResources.length ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Bookmark className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                <p className="text-muted-foreground">
                  {searchQuery
                    ? "No resources match your search."
                    : selectedFolderId
                    ? "No resources in this folder."
                    : "No saved resources yet."}
                </p>
                {!searchQuery && !selectedFolderId && (
                  <Link to="/library">
                    <Button variant="outline" className="mt-4">
                      Browse Library
                    </Button>
                  </Link>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {filteredResources.map((saved) => (
                <Card key={saved.id} className="group">
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between gap-2">
                      <CardTitle className="text-base font-medium line-clamp-2">
                        {saved.resource?.title || "Unknown Resource"}
                      </CardTitle>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
                          >
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={() => handleOpenMoveDialog(saved.id, saved.folder_id)}
                          >
                            <FolderOpen className="h-4 w-4 mr-2" />
                            Move to Folder
                          </DropdownMenuItem>
                          {saved.resource?.slug && (
                            <DropdownMenuItem asChild>
                              <Link to={`/library/${saved.resource.slug}`}>
                                <ExternalLink className="h-4 w-4 mr-2" />
                                View in Library
                              </Link>
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            className="text-destructive"
                            onClick={() => removeResourceMutation.mutate(saved.id)}
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Remove
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
                      {saved.resource?.description}
                    </p>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-muted-foreground capitalize">
                        {saved.resource?.type}
                      </span>
                      {saved.resource?.slug && (
                        <Link to={`/library/${saved.resource.slug}`}>
                          <Button variant="ghost" size="sm">
                            <ExternalLink className="h-4 w-4 mr-1" />
                            View
                          </Button>
                        </Link>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Rename Folder Dialog */}
      <Dialog open={!!editingFolder} onOpenChange={(open) => !open && setEditingFolder(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rename Folder</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <Input
              value={editingFolder?.name ?? ""}
              onChange={(e) =>
                setEditingFolder((f) => (f ? { ...f, name: e.target.value } : null))
              }
              onKeyDown={(e) =>
                e.key === "Enter" &&
                editingFolder &&
                updateFolderMutation.mutate({
                  id: editingFolder.id,
                  name: editingFolder.name.trim(),
                })
              }
            />
            <Button
              onClick={() =>
                editingFolder &&
                updateFolderMutation.mutate({
                  id: editingFolder.id,
                  name: editingFolder.name.trim(),
                })
              }
              disabled={!editingFolder?.name.trim() || updateFolderMutation.isPending}
            >
              Save
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Move to Folder Dialog */}
      {selectedResourceId && (
        <MoveToFolderDialog
          open={moveDialogOpen}
          onOpenChange={setMoveDialogOpen}
          savedResourceId={selectedResourceId}
          currentFolderId={selectedResourceFolderId}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ["my-collection"] });
          }}
        />
      )}
    </div>
  );
};

export default MyCollection;